"use client";

import { useState, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import { safeProviderNext } from "@/lib/auth/digestLink";
import { useAuth } from "@/lib/auth/AuthContext";
import { PROVIDER_DEMO_LOGINS } from "@/lib/api/provider";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import NicoleIcon from "@/components/NicoleIcon";

// One-tap demo accounts. ONLY rendered on the localhost entry path
// (window.location.hostname is localhost / 127.0.0.1); on the public host the
// section never mounts. All non-provider accounts use their own distinct
// strong password (the shared "Demo123!" secret was retired 2026-08-03); the
// provider accounts come from the canonical PROVIDER_DEMO_LOGINS registry
// (one button is rendered per entry, so adding a provider is a config-only
// change in lib/api/provider.ts — no edit here).
export const DEMO_ACCOUNTS: { label: string; email: string; password: string }[] = [
  { label: "Hub Admin", email: "admin@oneriver.ai", password: "IFj^7flxn^L&o3Bg" },
  { label: "Coordinator", email: "coordinator@oneriver.ai", password: "X#g#@oy5owtSCJHp" },
  { label: "Supervisor", email: "supervisor@oneriver.ai", password: "i&SbnNG!67rfdQf@" },
  { label: "Finance", email: "finance@oneriver.ai", password: "c#H7JSPGG$F@8qEH" },
  { label: "Biller", email: "biller@oneriver.ai", password: "Ods@L%gwH8xi!TKr" },
  { label: "Biller (Practice 2)", email: "saba@oneriver.ai", password: "TzhkFj$7yF2!zEj$" },
  { label: "Biller (Practice 3)", email: "biller3@oneriver.ai", password: "F!8ES7n!xzc7x0Q#" },
  { label: "AR Specialist", email: "rameez@oneriver.ai", password: "*PhP#5ERoXR!bfVi" },
  { label: "QA", email: "qa@oneriver.ai", password: "!v@h^8YnUwj9FAw4" },
  // Investor-demo tour login (read-only, names force-aliased). Listed here
  // because every other persona has a one-tap button, and its absence meant the
  // only way in was typing a 17-character password by hand.
  { label: "Demo (investor tour)", email: "demo@oneriver.ai", password: "Dem0-T0ur!2026#xR" },
  // Provider one-tap logins, generated from the registry (Chotani, Sheikh, …).
  ...PROVIDER_DEMO_LOGINS.map((p) => ({
    label: p.label,
    email: p.email,
    password: p.password,
  })),
];

// Whether the page is being served from the trusted localhost entry path.
// Backend mirrors this gate server-side (X-Forwarded-Host / X-Deosai-Public),
// so the demo buttons are only useful — and only shown — on localhost.
function isLocalHost(): boolean {
  return (
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname)
  );
}

// A no-op subscription: the localhost-ness of the page never changes for the
// lifetime of the mount, so we only need the getSnapshot/getServerSnapshot
// split. useSyncExternalStore resolves to the server value (false) during SSR
// and the real client value after hydration — no hydration mismatch and no
// setState-in-effect cascade.
const _noopSubscribe = () => () => {};
function useIsLocalHost(): boolean {
  return useSyncExternalStore(_noopSubscribe, isLocalHost, () => false);
}

// Where each role lands after login. Billers go straight to their workspace;
// providers (incl. the provider_admin super-user Dr. Basmaa) land in their
// /provider encounter workspace; everyone else keeps the executive dashboard
// default.
export function landingFor(roleKeys: string[]): string {
  // Demo FIRST, defensively. Today a demo login holds only the `demo` role, so
  // it would reach the /executive-dashboard default anyway. Ordering it here
  // means that if the role were ever combined with another (the demoAccess guard
  // deliberately still treats such a session as demo), login can't drop the user
  // on a route their own guard immediately bounces — which would look to them
  // like a failed sign-in rather than a redirect.
  if (roleKeys.includes("demo")) return "/executive-dashboard";
  if (roleKeys.includes("provider") || roleKeys.includes("provider_admin"))
    return "/provider";
  if (roleKeys.includes("biller")) return "/biller";
  if (roleKeys.includes("ar_specialist")) return "/ar";
  if (roleKeys.includes("qa")) return "/qa";
  return "/executive-dashboard";
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Resolved after hydration (window is unavailable during SSR) to avoid a
  // hydration mismatch; demo UI is gated on this being true.
  const [showDemoAccounts, setShowDemoAccounts] = useState(false);
  const isLocal = useIsLocalHost();
  const { login, user } = useAuth();
  const router = useRouter();

  const doLogin = async (loginEmail: string, loginPassword: string) => {
    setError("");
    setIsLoading(true);
    try {
      await login(loginEmail, loginPassword);
    } catch {
      setError("Invalid credentials. Please try again.");
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }
    await doLogin(email, password);
  };

  const handleDemoLogin = async (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    await doLogin(demoEmail, demoPassword);
  };

  if (user) {
    // `window` instead of `useSearchParams()`: the hook opts this route out of
    // static prerendering and fails the production build. This branch only runs
    // client-side after auth resolves, so `window` is available.
    const nextParam =
      typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.search).get("next");
    const dest =
      safeProviderNext(nextParam) ??
      landingFor((user.roles ?? []).map((r) => r.key));
    router.push(dest);
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-primary">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-200 border-t-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-primary flex flex-col">
      {/* Header */}
      <header className="px-8 py-6">
        <div className="flex items-center gap-3">
          <NicoleIcon size="sm" />
          <div>
            <p className="text-[13px] font-bold text-text-primary tracking-tight-heading">OneRiver</p>
            <p className="text-[10px] font-medium text-text-muted uppercase tracking-label">oneriver.ai</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-[400px]">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-5">
              <NicoleIcon size="xl" />
            </div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight-heading">OneRiver</h1>
            <p className="text-[13px] text-text-secondary mt-1.5">Revenue Uplift &amp; AI Operations for Healthcare</p>
          </div>

          <div className="bg-white rounded-card border border-border p-8 shadow-card">
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                error={!email && error ? " " : undefined}
              />
              <Input
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                error={!password && error ? " " : undefined}
              />
              {error && <p className="text-[13px] text-status-critical">{error}</p>}
              <Button type="submit" className="w-full" isLoading={isLoading}>
                Sign In
              </Button>
            </form>

            {/* Demo Access — localhost only. On the public host this block never
                mounts (isLocal stays false), matching the locked-down backend
                gate that rejects non-provider roles from the public edge. */}
            {isLocal && (
              <div className="mt-6 pt-6 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowDemoAccounts(!showDemoAccounts)}
                  className="w-full flex items-center justify-between text-[13px] text-text-secondary hover:text-text-primary transition-colors"
                >
                  <span className="font-medium">Demo Access</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${showDemoAccounts ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showDemoAccounts && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {DEMO_ACCOUNTS.map((acct) => (
                      <button
                        key={acct.email}
                        type="button"
                        onClick={() => handleDemoLogin(acct.email, acct.password)}
                        disabled={isLoading}
                        className="text-[12px] py-2 px-3 rounded-button border border-border text-text-secondary hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-colors disabled:opacity-50"
                      >
                        {acct.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-[11px] text-text-muted text-center mt-6">
            <a href="https://oneriver.ai" className="hover:text-primary-500 transition-colors">oneriver.ai</a>
            <span className="mx-2 text-border">·</span>
            Illustrative workflow for a company like XyberMed
          </p>
        </div>
      </main>

      <footer className="py-6 text-center">
        <div className="flex items-center justify-center gap-4 text-[11px] text-text-muted">
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Secure connection</span>
          </div>
          <span className="text-border">|</span>
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>HIPAA compliant</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
